import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardContent } from '../../../components/ui/card';
import { getAllPermissions, getRoleData, updateRolePermissions } from '../../../store/roles/actions';
import CheckModule from './CheckModule';
import { Button } from '../../../components/ui/button';
import { hasPermission } from '../../../commonUtils/permission.helper';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { getModuleName } from '../../../commonUtils/string.helper';
import ReviewBox from './ReviewBox';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator, } from "../../../components/ui/breadcrumb"

const ManageRolePermissions = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { roleKey } = useParams();

    const { loading, roleData, allPermissions } = useSelector((state) => state.Roles);
    const { role, permissions } = roleData;

    const [rolePermissions, setRolePermissions] = useState([]);
    const [moduleCheckboxes, setModuleCheckboxes] = useState({});
    const [showPermissionsReviewBox, setShowPermissionsReviewBox] = useState(false);
    const [reviewPermissions, setReviewPermissions] = useState({ old: {}, new: {} });

    // Fetch role and permissions data
    useEffect(() => {
        if (roleKey) {
            dispatch(getRoleData(roleKey));
            dispatch(getAllPermissions());
        }
    }, [dispatch, roleKey]);

    useEffect(() => {
        if (permissions) {
            setRolePermissions(permissions);
        }
    }, [permissions]);

    useEffect(() => {
        if (allPermissions) {
            const initialModuleCheckboxes = Object.keys(allPermissions).reduce((acc, module) => {
                const modulePermissionsIds = allPermissions[module].map(p => p._id);
                const isAllSelected = modulePermissionsIds.every(permissionId => rolePermissions.includes(permissionId));
                acc[module] = isAllSelected;
                return acc;
            }, {});
            setModuleCheckboxes(initialModuleCheckboxes);
        }
    }, [allPermissions, rolePermissions]);

    const permissionCheck = useCallback((val, permissionId, module) => {
        setRolePermissions((prevPermissions) => {
            const updatedPermissions = val 
                ? [...prevPermissions, permissionId] 
                : prevPermissions.filter(id => id !== permissionId);
            
            const isAllSelected = allPermissions[module].every(permission => updatedPermissions.includes(permission._id));
            setModuleCheckboxes((prevCheckboxes) => ({
                ...prevCheckboxes,
                [module]: isAllSelected,
            }));

            return updatedPermissions;
        });
    }, [allPermissions]);

    const handleModuleCheckbox = useCallback((module) => {
        const isChecked = !moduleCheckboxes[module];
        const modulePermissionsIds = allPermissions[module].map(p => p._id);

        setRolePermissions((prevPermissions) =>
            isChecked
                ? [...new Set([...prevPermissions, ...modulePermissionsIds])]
                : prevPermissions.filter(id => !modulePermissionsIds.includes(id))
        );

        setModuleCheckboxes((prevCheckboxes) => ({
            ...prevCheckboxes,
            [module]: isChecked,
        }));
    }, [moduleCheckboxes, allPermissions]);

    const savePermissions = useCallback(() => {
        if (role) {
            dispatch(updateRolePermissions({ role: {id: role._id, key: role.key}, permissions: rolePermissions }));
            handleCloseReviewBox();
        }
    }, [dispatch, role, rolePermissions]);

    const getPermissionTitlesByIds = useCallback((ids, allPermissions) => {
        const permissionsByModule = {};
        
        Object.entries(allPermissions).forEach(([module, modulePermissions]) => {
            const titles = modulePermissions
                .filter(permission => ids.includes(permission._id))
                .map(permission => permission.title);
            
            if (titles.length > 0) {
                const moduleName = getModuleName(module);
                permissionsByModule[moduleName] = titles;
            }
        });
        
        return permissionsByModule;
    }, []);

    useEffect(() => {
        if (allPermissions && permissions && rolePermissions) {
            const oldPermissionsByModule = getPermissionTitlesByIds(permissions, allPermissions);
            const newPermissionsByModule = getPermissionTitlesByIds(rolePermissions, allPermissions);
            setReviewPermissions({ old: oldPermissionsByModule, new: newPermissionsByModule });
        }
    }, [permissions, rolePermissions, allPermissions, getPermissionTitlesByIds]);

    const { tabs, tabContent, firstTabValue } = useMemo(() => {
        if (!allPermissions) return { tabs: [], tabContent: [], firstTabValue: null };

        const tabsArray = [];
        const tabContentArray = [];
        let firstValue = null;

        Object.entries(allPermissions).forEach(([module, modulePermissions], index) => {
            if (index === 0) firstValue = module;
            tabsArray.push(<TabsTrigger key={module} value={module} className="text-lg">{getModuleName(module)}</TabsTrigger>);
            tabContentArray.push(
                <TabsContent key={module} value={module} className="m-0">
                    <CheckModule
                        module={module}
                        modulePermissions={modulePermissions}
                        moduleChecked={moduleCheckboxes[module] || false}
                        onModuleCheck={() => handleModuleCheckbox(module)}
                        rolePermissions={rolePermissions}
                        onPermissionCheck={(val, permissionId) => permissionCheck(val, permissionId, module)}
                    />
                </TabsContent>
            );
        });

        return { tabs: tabsArray, tabContent: tabContentArray, firstTabValue: firstValue };
    }, [allPermissions, rolePermissions, moduleCheckboxes, permissionCheck, handleModuleCheckbox]);

    // Show and hide review box
    const showReviewBox = () => setShowPermissionsReviewBox(true);
    const handleCloseReviewBox = () => setShowPermissionsReviewBox(false);

    return (
        <>
            <Card className="w-full mb-2 overflow-hidden">
            <CardContent>
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                        <BreadcrumbLink href="/roles">Manage Roles</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                        <BreadcrumbPage>Role Permissions for {role?.title}</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                    </Breadcrumb>
                        {!loading && <Tabs defaultValue={firstTabValue}>
                        <div className='w-full bg-accent my-2 p-2 overflow-hidden overflow-x-scroll'>
                            <TabsList>
                                {tabs}
                            </TabsList>
                        </div>
                        {tabContent}
                        <hr className='mb-4'/>
                        {hasPermission('roles.permissions_update') && <div className='flex justify-end'>
                            <Button variant="outline" onClick={() => navigate("/roles")} disabled={loading}>Back</Button>
                            <Button className="ml-2" onClick={showReviewBox} disabled={loading}>Save</Button>
                        </div>}
                </Tabs>}
                </CardContent>
            </Card>
            <ReviewBox
                open={showPermissionsReviewBox}
                handleClose={handleCloseReviewBox}
                confirmAction={savePermissions}
                reviewPermissions={reviewPermissions}
            />
        </>
    );
};

export default ManageRolePermissions;
